<template lang="pug">
.account-details-settings
  CardMinimal(title="Accounts and users")
    .account-buttons.p-a-4
      Button(@click="showConnectedAccounts = true" icon="parent-account-24") Connected accounts
      Button(@click="showUsers = true" icon="users-24") Users
  CardMinimal(title="Account details").m-t-4
    .p-x-4.p-y-3
      p.m-b-1 {{ $t('account_information.account_name') }}
      FormInput(type="text", v-model="accountData.name", id="account-name", :outline="true")
      p.m-b-1.m-t-2 {{ $t('account_information.company_name') }}
      FormInput(type="text", v-model="accountData.company.name", id="account-companyname", :outline="true")
      small.blue {{ $t('account_information.company_name_description') }}
      p.m-b-1.m-t-2 {{ $t('account_information.tax_id') }}
      FormInput(type="text", v-model="accountData.company.tax_id", id="account-tax-id", :outline="true")
      .d-f.m-t-2
        div.m-r-1.f-1
          p.m-b-1 {{ $t('account_information.postal_code') }}
          FormInput(type="text", v-model="accountData.company.address.postal_code", id="account-postal-code", :outline="true")
        div.m-l-1.f-1
          p.m-b-1 {{ $t('account_information.country') }}
          p {{ accountData.company.address.country ? accountCountries[$i18n.locale].find(country => country.value === accountData.company.address.country.toLowerCase()).string : ''}}
      .d-f.m-t-2
        div.m-r-1.f-1
          p.m-b-1 {{ $t('account_information.address') }}
          FormInput(type="text", :placeholder="$t('account_information.line1')" v-model="accountData.company.address.line1", id="account-address", :outline="true").m-b-2
          FormInput(type="text", :placeholder="$t('account_information.line2')" v-model="accountData.company.address.line2", id="account-address2", :outline="true")
        div.m-l-1.f-1
          p.m-b-1 {{ $t('account_information.city') }}
          FormInput(type="text", v-model="accountData.company.address.city", id="account-city", :outline="true")
      div.m-t-4.d-f
        Button(@click="validDetails ? updateDetails() : null" :disabled="!validDetails" :loading="updateState === 'updating'")
          template(v-if="updateState === ''") {{ $t('settings.update_account_information') }}
          template(v-else-if="updateState === 'updating'") {{ $t('settings.updating_account_information') }}
          template(v-else-if="updateState === 'updated'") {{ $t('settings.account_information_updated') }}
  CardMinimal(title="Password").m-t-4
    .p-a-4
      div.d-f.f-g.fd-c.fd-md-r
        div.f-1
          p New password
          FormInput(type="password", placeholder="********", v-model="newPassword.password", :error="newPassword.passwordError", id="password", :outline="true", @validate="validatePasswordFunc('password')").m-t-1
        div.f-1
          p New password again
          FormInput(type="password", placeholder="********", v-model="newPassword.again", :error="newPassword.againError", id="password", :outline="true", @validate="validatePasswordFunc('again')").m-t-1
      Button(@click="validPassword ? updatePassword() : null", :disabled="!validPassword" :loading="newPassword.state === 'updating'" :class="`m-t-1 ${newPassword.state === 'updated' ? 'green' : ''}`")
        template(v-if="newPassword.state === 'updated'") Password updated
        template(v-else) Update password
  ConnectedAccounts(:show="showConnectedAccounts" @close="showConnectedAccounts = false")
  Users(:show="showUsers" @close="showUsers = false")
</template>

<script>

import Toggle from '@/components/toggle.vue';
import ConnectedAccounts from './ConnectedAccounts.vue';
import Users from './Users.vue';

export default {
  components: {
    ConnectedAccounts,
    Users,
    Toggle,
  },
  computed: {
    /**
     * Validate all the details
     * @returns {Boolean}
     */
    validDetails() {
      const {
        accountData: {
          name,
          company: {
            name: companyName,
          },
        },
      } = this;

      return name.length > 2 && companyName !== '';
    },
    validPassword() {
      const {
        newPassword: {
          password,
          again,
        },
      } = this;

      if (password.length < 8) {
        return false;
      }

      if (password !== again) {
        return false;
      }

      return true;
    },
  },
  methods: {
    async updateSettings(key, value) {
      const { featuresEnabled } = this;

      const settings = {
        ppv: featuresEnabled.includes('ppv'),
        shopping: featuresEnabled.includes('lvs'),
      };

      settings[key] = value;

      await this.$store.dispatch('AccountManager/updateSettings', settings);
      await this.$store.dispatch('AccountManager/getAccountSettings');
    },
    /**
     * Set accountinformation where it needs to be
     */
    setAccountInformation() {
      if (!this.account) {
        return;
      }

      const { account: { name, company } } = this;

      this.accountData.name = name;
      this.accountData.company = typeof company.address === 'undefined' ? { ...company, address: {} } : company;
    },
    /**
     * Update details
     */
    async updateDetails() {
      const { validDetails, accountData: { name, company: { name: companyName, tax_id, address } } } = this;
      if (!validDetails) {
        return;
      }

      this.updateState = 'updating';

      const payload = {
        name,
        company: {
          name: companyName,
          address,
        },
      };

      if (tax_id !== '') {
        payload.company.tax_id = tax_id;
      }

      const result = await this.$store.dispatch('AccountManager/updateAccount', payload);

      if (!result) {
        this.updateState = '';
        swal({ title: 'An error occured', text: 'For some reason we could not save this information right now', icon: 'error' });
        return;
      }

      this.updateState = 'updated';
      setTimeout(() => {
        this.updateState = '';
      }, 2000);
    },
    validatePasswordFunc(type) {
      const {
        newPassword: {
          password,
          again,
        },
      } = this;

      if (type === 'password') {
        if (password.length < 8) {
          this.newPassword.passwordError = 'Password not long enough';
          return;
        }

        this.newPassword.passwordError = '';

        if (again !== '') {
          this.validatePasswordFunc('again');
        }
      }

      if (type === 'again') {
        if (password !== again) {
          this.newPassword.againError = 'Not the same as password';
          return;
        }
        this.newPassword.againError = '';
      }
    },
    async updatePassword() {
      const update = await swal({
        title: this.$t('errors.are_you_sure'), text: 'This will set a new password for your user account', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!update) {
        return;
      }

      this.newPassword.state = 'updating';

      const updated = await this.$store.dispatch('User/changePassword', this.newPassword.password);

      if (!updated) {
        swal({ title: 'An error occured', text: 'For some reason we could not set a new password right now', icon: 'error' });
        this.newPassword.state = '';
        return;
      }

      this.newPassword.state = 'updated';

      this.newPassword.timeout = setTimeout(() => {
        this.newPassword.state = '';
        this.newPassword.password = '';
        this.newPassword.again = '';
      }, 2000);
    },
  },
  watch: {
    account: {
      immediate: true,
      handler() {
        this.setAccountInformation();
      },
    },
  },
  data() {
    return {
      showConnectedAccounts: false,
      showUsers: false,
      updateState: '',
      accountData: {
        name: '',
        company: {
          name: '',
          tax_id: '',
          address: {},
        },
      },
      newPassword: {
        password: '',
        again: '',
        passwordError: '',
        againError: '',
        state: '',
        timeout: null,
      },
    };
  },
  beforeDestroy() {
    clearTimeout(this.newPassword.timeout);
  },
};
</script>

<style lang="scss" scoped>
.account-features {
  & > * {
    padding: 10px 0;

    &:not(:last-child) {
      border-bottom: 1px solid #f1f1f1;
    }
  }
}

.connected {
  margin-bottom: 5px;
}

.account-buttons {
  display: flex;
  flex-direction: column;

  & > * {
    margin: 6px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (min-width: 768px) {
    align-items: center;
    flex-direction: row;
    max-width: 800px;
    margin: 0 auto;

    & > * {
      width: 50%;
      margin: 0 12px;
    }
  }
}
</style>
